import { db } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  serverTimestamp
} from "firebase/firestore";

/**
 * Create a new label for the user
 */
export const createLabel = async (userId, labelName, labelColor) => {
  try {
    const labelsRef = collection(db, "labels");
    const docRef = await addDoc(labelsRef, {
      name: labelName,
      color: labelColor,
      userId: userId,
      createdAt: serverTimestamp()
    });
    return { id: docRef.id, name: labelName, color: labelColor, userId };
  } catch (error) {
    console.error("Error creating label:", error);
    throw error;
  }
};

/**
 * Get all labels for a specific user
 */
export const getUserLabels = async (userId) => {
  try {
    const labelsRef = collection(db, "labels");
    const q = query(labelsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);

    const labels = [];
    querySnapshot.forEach((doc) => {
      labels.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return labels;
  } catch (error) {
    console.error("Error fetching labels:", error);
    throw error;
  }
};

/**
 * Update an existing label
 */
export const updateLabel = async (labelId, labelName, labelColor) => {
  try {
    const labelRef = doc(db, "labels", labelId);
    await updateDoc(labelRef, {
      name: labelName,
      color: labelColor
    });
    return { id: labelId, name: labelName, color: labelColor };
  } catch (error) {
    console.error("Error updating label:", error);
    throw error;
  }
};

/**
 * Delete a label
 */
export const deleteLabel = async (labelId) => {
  try {
    const labelRef = doc(db, "labels", labelId);
    await deleteDoc(labelRef);
  } catch (error) {
    console.error("Error deleting label:", error);
    throw error;
  }
};

/**
 * Assign labels to a case
 */
export const assignLabelsToCase = async (collectionName, caseId, labelIds, userId) => {
  try {
    const caseRef = doc(db, collectionName, caseId);

    // Get current case data to update labelsByUser
    const caseDoc = await getDoc(caseRef);
    const caseData = caseDoc.data();
    const labelsByUser = caseData?.labelsByUser || {};

    // Update the user's labels in the mapping
    if (userId) {
      labelsByUser[userId] = labelIds;
    }

    await updateDoc(caseRef, {
      labels: labelIds,
      labelsByUser: labelsByUser
    });
  } catch (error) {
    console.error("Error assigning labels to case:", error);
    throw error;
  }
};

/**
 * Remove labels from a case
 */
export const removeLabelsFromCase = async (collectionName, caseId) => {
  try {
    const caseRef = doc(db, collectionName, caseId);
    await updateDoc(caseRef, {
      labels: []
    });
  } catch (error) {
    console.error("Error removing labels from case:", error);
    throw error;
  }
};

/**
 * Copy case labels to a recipient user
 * If the recipient doesn't have a label with the same name and color, create it for them
 * Returns a mapping of original labelIds to new labelIds for the recipient
 */
export const copyLabelsToUser = async (ownerUserId, recipientUserId, labelIds) => {
  try {
    if (!labelIds || labelIds.length === 0) {
      return {}; // No labels to copy
    }

    // Get the owner's labels
    const ownerLabels = await getUserLabels(ownerUserId);
    const ownerLabelsMap = ownerLabels.reduce((acc, label) => {
      acc[label.id] = label;
      return acc;
    }, {});

    // Get the recipient's existing labels
    const recipientLabels = await getUserLabels(recipientUserId);

    // Create a mapping of originalLabelId -> recipientLabelId
    const labelMapping = {};

    for (const labelId of labelIds) {
      const ownerLabel = ownerLabelsMap[labelId];
      if (!ownerLabel) continue;

      // Check if recipient already has a label with the same name and color
      const existingLabel = recipientLabels.find(
        (l) => l.name === ownerLabel.name && l.color === ownerLabel.color
      );

      if (existingLabel) {
        // Recipient already has this label
        labelMapping[labelId] = existingLabel.id;
      } else {
        // Create the label for the recipient
        const newLabel = await createLabel(
          recipientUserId,
          ownerLabel.name,
          ownerLabel.color
        );
        labelMapping[labelId] = newLabel.id;
        recipientLabels.push(newLabel); // Add to local array for future lookups
      }
    }

    return labelMapping;
  } catch (error) {
    console.error("Error copying labels to user:", error);
    throw error;
  }
};

/**
 * Get label by ID
 */
export const getLabelById = async (labelId) => {
  try {
    const labelRef = doc(db, "labels", labelId);
    const labelDoc = await getDoc(labelRef);
    if (labelDoc.exists()) {
      return { id: labelDoc.id, ...labelDoc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting label by ID:", error);
    throw error;
  }
};
